import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SplitListPanelStoreContext } from '../context';
import { useListPanelStore } from '../store/listPanelStore';

import type { PropsWithChildren } from 'react';
import type { StoreApi } from 'zustand';
import type { ListPanelLayoutGuardState, ListPanelPreferenceState, ListPanelStore } from '../types';

interface SplitListPanelProviderProps extends PropsWithChildren {
  initialLayoutGuard?: Partial<ListPanelLayoutGuardState>;
  initialPreference?: Partial<ListPanelPreferenceState>;
  pageKey?: string;
}

// Extended context value with panel preferences
export interface SplitListPanelContextValue {
  availablePanel: boolean;
  enablePanel: boolean;
  setEnablePanel: (enable: boolean) => void;
  store: StoreApi<ListPanelStore>;
}

/**
 * SplitListPanelProvider
 *
 * Creates a zustand store instance for managing list panel selection.
 * Uses React Context to provide the store to child components.
 *
 * Features:
 * - Instance-based store per Provider (not global singleton)
 * - Multi-select support with enableMultiSelect preference
 * - Selection state management (selectedIds, focusedId)
 * - Derived state (hasSelection, selectionCount, etc.)
 * - Layout guard configuration (min/max/default panel width)
 *
 * @param pageKey - Unique identifier for devtools (optional)
 * @param initialPreference - Initial preference configuration
 * @param initialLayoutGuard - Initial layout guard configuration
 * @param children - Child components (list content area)
 *
 * @example
 * ```tsx
 * <SplitListPanelProvider pageKey="dns-zones">
 *   <ZoneList />
 * </SplitListPanelProvider>
 * ```
 */
export default function SplitListPanelProvider({
  pageKey: _pageKey,
  initialPreference,
  initialLayoutGuard,
  children,
}: SplitListPanelProviderProps) {
  const storeRef = useRef<StoreApi<ListPanelStore> | null>(null);

  if (!storeRef.current) {
    storeRef.current = useListPanelStore;
  }

  // Apply initial configurations
  useEffect(() => {
    if (!storeRef.current) return;

    if (initialPreference) {
      storeRef.current.getState().setPreference(initialPreference);
    }

    if (initialLayoutGuard) {
      storeRef.current.getState().setLayoutGuard(initialLayoutGuard);
    }
  }, [initialPreference, initialLayoutGuard]);

  // Panel preferences state
  const [panelPreferences, setPanelPreferences] = useState({
    enablePanel: false,
    availablePanel: true,
  });

  // Method to update panel enable state
  const setEnablePanel = useCallback((enable: boolean) => {
    setPanelPreferences(prev => ({ ...prev, enablePanel: enable }));
  }, []);

  const contextValue = useMemo<SplitListPanelContextValue>(() => {
    if (!storeRef.current) {
      throw new Error('Store not initialized');
    }

    return {
      store: storeRef.current,
      enablePanel: panelPreferences.enablePanel,
      availablePanel: panelPreferences.availablePanel,
      setEnablePanel,
    };
  }, [panelPreferences.enablePanel, panelPreferences.availablePanel, setEnablePanel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (storeRef.current) {
        storeRef.current.getState().reset();
      }
    };
  }, []);

  return (
    <SplitListPanelStoreContext.Provider value={contextValue}>
      {children}
    </SplitListPanelStoreContext.Provider>
  );
}
